import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { readItemUsage, type ItemReader } from "../../../api/content/authoring/itemUsageApi";
import { listProjectItems, readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import ItemReferenceName from "../ItemReferenceName";
import ArtifactContentView from "./ArtifactContentView";
import ArtifactVersions from "./ArtifactVersions";
import MadeByText from "./MadeByText";
import Chip from "../../../ui/feedback/Chip";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import EmptyState from "../../../ui/feedback/EmptyState";
import { itemKindText } from "../../workflows/inputCards/typeWords";

/**
 * The artifact page's own parts (authoring-ui.md AU-5, mockup `new-artifact.html` part 2):
 * the content, readable; who or what made this version; the exact versions it was made
 * from, marked when a newer one exists; its versions, each with its diff; and what reads it.
 */
export default function ArtifactDetails({ projectId, itemId, reference }: { projectId: string; itemId: string; reference: string }) {
  const { t } = useTranslation();
  const load = useCallback(
    (signal: AbortSignal) => Promise.all([readAuthoredItem(reference, signal), readItemUsage(projectId, itemId, signal), listProjectItems(projectId, null, signal)]),
    [projectId, itemId, reference],
  );
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("authoring.artifactPage.loading")} onRetry={reload} />;
  const [item, usage, main] = state.value;
  if (item.content?.kind !== "artifact") {
    return null;
  }
  const artifact = item.content.definition;
  const latestByItem = new Map(main.items.map((listed) => [listed.itemId, listed.reference]));
  const current = usage.versions[0]?.reference === reference;
  return (
    <div className="space-y-4">
      <p className="text-xs">
        <Chip tone={current ? "neutral" : "info"}>{current ? t("authoring.artifactPage.current") : t("authoring.artifactPage.notCurrent")}</Chip>
        {current || usage.versions[0] === undefined ? null : (
          <Link to={appPaths.item(usage.versions[0].reference)} className="app-link ml-2 underline">{t("authoring.artifactPage.openCurrent")}</Link>
        )}
      </p>
      <section className="app-card space-y-2 p-3">
        <h3 className="app-title text-base font-semibold">{t("authoring.artifactPage.content")}</h3>
        <p className="app-text-muted text-xs">{artifact.media_type}, {artifact.schema}</p>
        <ArtifactContentView artifact={artifact} />
      </section>
      <section className="app-card space-y-2 p-3">
        <h3 className="app-title text-base font-semibold">{t("authoring.artifactPage.madeBy")}</h3>
        <MadeByText madeBy={item.madeBy} />
      </section>
      <section className="app-card space-y-2 p-3">
        <h3 className="app-title text-base font-semibold">{t("authoring.artifactPage.madeFrom")}</h3>
        {artifact.derived_from.length === 0 ? <EmptyState>{t("authoring.artifactPage.byHand")}</EmptyState> : null}
        <ul className="space-y-1 text-sm">
          {artifact.derived_from.map((source) => {
            const latest = latestByItem.get(source.split("@")[0] ?? source);
            const outdated = latest !== undefined && latest !== source && source.includes("@");
            return (
              <li key={source} className="flex flex-wrap items-center gap-2">
                {source.includes("@") ? <ItemReferenceName reference={source} /> : <span className="font-mono text-xs">{source}</span>}
                {outdated ? <Chip tone="info">{t("authoring.artifactPage.newerExists")}</Chip> : null}
                {outdated ? <ItemReferenceName reference={latest} /> : null}
              </li>
            );
          })}
        </ul>
        {artifact.derived_from.some((source) => latestByItem.get(source.split("@")[0] ?? source) !== source && source.includes("@")) ? <p className="app-help-text text-xs">{t("authoring.artifactPage.outdatedHint")}</p> : null}
      </section>
      <ArtifactVersions versions={usage.versions} shown={reference} />
      <section className="app-card space-y-2 p-3">
        <h3 className="app-title text-base font-semibold">{t("authoring.artifactPage.readBy")}</h3>
        {usage.readBy.length === 0 ? <EmptyState>{t("authoring.artifactPage.readByNone")}</EmptyState> : null}
        <ul className="space-y-1 text-sm">
          {usage.readBy.map((reader) => <ReaderLine key={`${reader.reference} ${reader.readKind}`} reader={reader} />)}
        </ul>
      </section>
    </div>
  );
}

function ReaderLine({ reader }: { reader: ItemReader }) {
  const { t } = useTranslation();
  const path = reader.kind === "workflow" ? appPaths.workflow(reader.reference) : appPaths.item(reader.reference);
  return (
    <li className="flex flex-wrap items-center gap-2">
      <Chip>{itemKindText(reader.kind, t)}</Chip>
      <Link to={path} className="app-link underline">{reader.name}</Link>
      <span className="app-text-muted text-xs">
        {t(`authoring.artifactPage.reads.${reader.readKind}`)}
        {reader.readKind === "selector" || reader.readKind === "bundled" ? `, ${reader.selectedVersion === null ? t("authoring.artifactPage.floating") : t("authoring.artifactPage.pinned")}` : ""}
      </span>
      {reader.via === null ? null : (
        <span className="text-xs">
          {t("authoring.artifactPage.via")} <ItemReferenceName reference={reader.via} />
        </span>
      )}
    </li>
  );
}
